import type { FileCloneStatus } from '@/types/poc/helia';
import type { CloningProgress } from '@/store/slices/poc/cloneQueueSlice';
import { formatFileSize } from '@/utils/formatters.util';
import './CloneStatusIndicator.scss';

export interface CloneStatusIndicatorProps {
  status: FileCloneStatus;
  error?: string;
  progress?: CloningProgress;
}

const STATUS_LABELS: Record<FileCloneStatus, string> = {
  cloned: 'Ready to decrypt and download',
  cloning: 'Transferring to browser',
  pending: 'Waiting to be transferred',
  failed: 'Transfer failed',
  unknown: '',
  'no-access': 'No decryption access',
};

function getCloningLabel(progress?: CloningProgress): string {
  if (progress === undefined) return STATUS_LABELS.cloning;

  const loaded = formatFileSize(progress.bytesLoaded);
  if (progress.totalBytes > 0) {
    return `Transferring: ${loaded} / ${formatFileSize(progress.totalBytes)}`;
  }
  return `Transferring: ${loaded}`;
}

export function CloneStatusIndicator({ status, error, progress }: CloneStatusIndicatorProps) {
  // Don't render anything for unknown status
  if (status === 'unknown') {
    return null;
  }

  const label = status === 'failed' && error != null
    ? `Transfer failed: ${error}`
    : status === 'cloning'
      ? getCloningLabel(progress)
      : STATUS_LABELS[status];

  return (
    <span className={`clone-status-indicator clone-status-indicator--${status}`} title={label}>
      <span className="clone-status-indicator__icon" aria-hidden="true" />
      <span className="clone-status-indicator__label">{label}</span>
    </span>
  );
}

export default CloneStatusIndicator;
